import { Trans } from '@lingui/react/macro';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { LATEST_MATCHES_FRESH_SECONDS } from 'modules/main/domain/const/latest-matches';
import { useLatestMatchesQuery } from 'modules/main/store/latest-matches-api';
import React from 'react';
import { useSelector } from 'react-redux';
import { deriveAsyncState } from 'shared/utils/async-state';
import UDButton from 'ud-ui/components/button';
import UDCard from 'ud-ui/components/card';
import UDEmptyState from 'ud-ui/components/empty-state';
import { SkeletonPreview } from 'ud-ui/components/skeleton';
import UDText from 'ud-ui/components/text';
import NewsTicker, { NewsTickerSkeleton } from './components';

interface IProps {
  className?: string;
}

/**
 * Бегущая строка последних матчей организации на главной — данные из нашей базы (`previousMatches`).
 *
 * Это плашка, а не карточка раздела: у неё нет страницы «Подробнее» и нет высоты `Section` (244 px),
 * которая оставила бы под одной строкой пустое поле. Запись — эмблемы команд, время, результат; клик ведёт
 * на страницу матча внутри админки. Лента кэшируется на `LATEST_MATCHES_FRESH_SECONDS`, повторный заход
 * на главную в это окно сеть не трогает.
 */
function SectionNews(props: IProps) {
  const { className } = props;
  const organizerId = useSelector(authStoreUserTournamentId);
  const { data, currentData, isFetching, isError, refetch } = useLatestMatchesQuery(organizerId ?? 0, {
    skip: !organizerId,
    refetchOnMountOrArgChange: LATEST_MATCHES_FRESH_SECONDS,
  });

  const matches = data ?? [];
  const state = deriveAsyncState({ itemsCount: matches.length, hasCurrentData: currentData !== undefined, isFetching, isError });

  return (
    <UDCard as="section" padding="none" className={`flex min-h-24 items-center gap-5 overflow-hidden px-6 ${className ?? ''}`}>
      {/* Видимой подписи нет — строка говорит сама за себя; заголовок остаётся для скринридера. */}
      <UDText as="h2" variant="title-3" className="sr-only">
        <Trans>Последние матчи</Trans>
      </UDText>

      <div className="min-w-0 flex-1 py-2">
        {state.status === 'loading' && <NewsTickerSkeleton />}

        {state.status === 'error' && (
          <UDEmptyState
            className="py-2"
            title={<Trans>Не удалось загрузить матчи</Trans>}
            action={
              <UDButton variant="secondary" onClick={() => refetch()}>
                <Trans>Повторить</Trans>
              </UDButton>
            }
          />
        )}

        {state.status === 'empty' && <UDEmptyState className="py-2" title={<Trans>Матчей пока нет</Trans>} />}

        {state.status === 'ready' && (
          <SkeletonPreview skeleton={<NewsTickerSkeleton />}>
            <NewsTicker matches={matches} />
          </SkeletonPreview>
        )}
      </div>
    </UDCard>
  );
}

export default SectionNews;
